/* Greenleaf — the mobile sticky purchase bar.
 *
 * Loaded from sections/gl-product.liquid, only when the piece is available
 * (a sold page has no buy action to keep in view) and independently of
 * whether the piece has any media — a product with zero photographs still
 * has a purchase column, so this cannot live inside gl-media.js, which only
 * loads when the stage renders.
 *
 * TWO WATCHERS, NOT A SCROLL-POSITION GUESS.
 *   - [data-cta-anchor] is the real Add to bag / Ask row. The bar shows
 *     whenever that row is not on screen — above OR below the viewport —
 *     agreeing with wherever the button actually is, whatever the
 *     description or spec groups run to, rather than a hardcoded pixel
 *     figure that drifts the moment copy changes.
 *
 *     "Either direction" since Package 19 (11 Sep 2026). It used to show only
 *     once the row had scrolled ABOVE the top, on the argument that a
 *     customer should not meet the bar before the button. Measured on a 390 x
 *     844 phone: the real button sat at y=884, so the first screen of a
 *     $17,550 ring had no way to buy or ask on it at all, and the bar waited
 *     for a scroll past a button the customer had not seen. Now the bar is
 *     there on load and goes away exactly while the real row is in view.
 *   - The footer is the other watcher. The brief is explicit that this must
 *     not cover the footer, and "stop showing before the footer arrives" is
 *     the only way to keep that promise without measuring anything by hand.
 *
 * `hidden` IS THE MECHANISM, not a class that toggles opacity. A bar that is
 * merely invisible is still in the accessibility tree, and a screen-reader
 * user scrolling this page does not need to meet "Add to bag" twice.
 */
(function () {
  'use strict';

  function init() {
    var bar = document.querySelector('[data-sticky-bar]');
    var anchor = document.querySelector('[data-cta-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var footer = document.querySelector('footer.foot') || document.querySelector('footer');

    var anchorOff = false;     // the real button row is not on screen
    var footerNear = false;    // the footer is on screen (or about to be)

    function update() {
      if (anchorOff && !footerNear) bar.removeAttribute('hidden');
      else bar.setAttribute('hidden', '');
    }

    // Off screen in either direction counts. The observer fires once on
    // observe(), so on a phone where the row starts below the fold the bar
    // is up before the first scroll.
    // "On screen" means most of the row, not its first pixel. Measured on a
    // 390 x 844 phone, 18 Sep 2026: the row's top edge sat at y=811, so 33
    // of its 108 pixels were inside the viewport, `isIntersecting` was true,
    // and the bar stayed hidden on a first screen that showed no usable
    // button at all. The bar now waits until 60% of the row is visible.
    new IntersectionObserver(function (entries) {
      anchorOff = entries[entries.length - 1].intersectionRatio < 0.6;
      update();
    }, { threshold: [0, 0.6, 1] }).observe(anchor);

    if (footer) {
      // A 20% run-up rather than waiting for the footer's top edge to be
      // exactly at the viewport's bottom: the bar sliding away exactly as the
      // footer's first pixel appears reads as the bar being SHOVED off by the
      // footer arriving, not as the two cooperating.
      // The margin is POSITIVE, which grows the watched area 20% BELOW the
      // screen, so the footer counts as near before it arrives. It was -20%,
      // which shrinks the area instead: the footer had to be 20% of the way
      // up the screen before the bar hid, so on a 390 x 844 phone the bar sat
      // on the footer's heading for about 170px of scrolling, in both
      // directions (measured 1 Oct 2026, prompted by an outside review).
      new IntersectionObserver(function (entries) {
        footerNear = entries[entries.length - 1].isIntersecting;
        update();
      }, { rootMargin: '0px 0px 20% 0px' }).observe(footer);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
